import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { useChat } from "@/context/ChatContext";
import { BarChart3, Boxes, ClipboardList, DatabaseBackup, DoorOpen, FileText, Grid3x3, KeyRound, LayoutDashboard, LogOut, Menu,
  ListChecks, Landmark, MessagesSquare, PieChart, PlusCircle, Receipt, ScanLine, Search, Settings, Smartphone, ShieldCheck, Users, UserRound, Wallet, Warehouse, WifiOff, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABEL } from "@/lib/api";

export const NAV = [
  { to: "/dashboard", label: "لوحة التحكم", Icon: LayoutDashboard, perms: ["view_dashboard"] },
  { to: "/pen", label: "تسجيل اختيار كبش", Icon: Smartphone, perms: ["record_selection"] },
  { to: "/sales/new", label: "بيع جديد", Icon: PlusCircle, perms: ["create_sale"] },
  { to: "/selections", label: "اختيارات الحضيرة", Icon: ListChecks, perms: ["create_sale", "manage_selections"] },
  { to: "/sales", label: "المبيعات والأرصدة", Icon: ClipboardList, perms: ["view_sales"] },
  { to: "/payments", label: "المدفوعات", Icon: Wallet, perms: ["view_sales"] },
  { to: "/treasuries", label: "الخزائن", Icon: Landmark, perms: ["view_all_treasuries", "manage_treasuries", "record_payment", "manage_expenses"] },
  { to: "/treasury-reports", label: "تقارير الخزائن", Icon: PieChart, perms: ["view_all_treasuries", "manage_treasuries", "record_payment", "manage_expenses"] },
  { to: "/customers", label: "الزبائن", Icon: UserRound, perms: ["manage_customers", "create_sale"] },
  { to: "/permits", label: "تصاريح الخروج", Icon: FileText, perms: ["issue_permit", "cancel_permit"] },
  { to: "/barn", label: "الحظيرة والتسليم", Icon: Warehouse, perms: ["confirm_delivery", "view_my_sections"] },
  { to: "/gate", label: "بوابة الخروج", Icon: DoorOpen, perms: ["confirm_exit"] },
  { to: "/verify", label: "التحقق من تصريح", Icon: ScanLine, perms: ["verify_permit"], hideIf: ["confirm_exit", "confirm_delivery"] },
  { to: "/lookup", label: "البحث عن كبش أو وصل", Icon: Search, perms: ["lookup_sheep", "view_sales", "verify_permit", "record_selection"] },
  { to: "/chat", label: "الدردشة", Icon: MessagesSquare, perms: [] },
  { to: "/assistant", label: "المساعد الذكي", Icon: Sparkles, perms: [] },
  { to: "/purchases", label: "المشتريات والمخزون", Icon: Boxes, perms: ["manage_inventory"] },
  { to: "/sections", label: "أقسام الحظيرة", Icon: Grid3x3, perms: ["manage_sections"] },
  { to: "/expenses", label: "المصاريف", Icon: Receipt, perms: ["manage_expenses"] },
  { to: "/reports", label: "التقارير", Icon: BarChart3, perms: ["view_dashboard"] },
  { to: "/users", label: "المستخدمون والصلاحيات", Icon: Users, perms: ["manage_users"] },
  { to: "/settings", label: "الإعدادات وفترة التسليم", Icon: Settings, perms: ["manage_settings"] },
  { to: "/audit", label: "سجل العمليات", Icon: ShieldCheck, perms: ["view_audit"] },
  { to: "/backup", label: "النسخ الاحتياطي", Icon: DatabaseBackup, perms: ["backup"] },
];

function useOnline() {
  const [on, setOn] = useState(navigator.onLine);
  useEffect(() => {
    const a = () => setOn(true), b = () => setOn(false);
    window.addEventListener("online", a); window.addEventListener("offline", b);
    return () => { window.removeEventListener("online", a); window.removeEventListener("offline", b); };
  }, []);
  return on;
}

const Brand = ({ logo, name }) => (
  <div className="flex items-center gap-3">
    {logo ? <img src={logo} alt="" className="w-11 h-11 rounded-xl object-contain bg-white" /> :
      <div className="w-11 h-11 rounded-xl bg-[#D97706] text-white grid place-items-center font-black font-display text-lg">ر</div>}
    <div className="leading-tight">
      <div className="font-display font-extrabold text-lg text-white">الربيع للأضاحي</div>
      <div className="text-[11px] text-emerald-100/80 tracking-wide">{name && name !== "الربيع للأضاحي" ? name : "نظام إدارة بيع الأضاحي"}</div>
    </div>
  </div>
);

export default function Layout() {
  const { user, logout, can, settings } = useAuth();
  const [open, setOpen] = useState(false);
  const online = useOnline();
  const nav = useNavigate();
  const items = NAV.filter((n) => (!n.perms.length || can(...n.perms)) && !(n.hideIf && can(...n.hideIf)));
  const { unread } = useChat();

  const Side = (
    <nav className="flex flex-col gap-1 p-3">
      {items.map(({ to, label, Icon }) => (
        <NavLink key={to} to={to} end={to === "/sales"} onClick={() => setOpen(false)} data-testid={`nav-${to.replace(/\//g, "-").slice(1)}`}
          className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 h-12 font-bold text-[15px] transition-colors ${isActive ? "bg-white text-[#1A4D2E]" : "text-emerald-50 hover:bg-white/10"}`}>
          <Icon className="w-5 h-5 shrink-0" /> {label}
          {to === "/chat" && unread > 0 && <span className="mr-auto min-w-6 h-6 px-1.5 rounded-full bg-[#D97706] text-white text-xs grid place-items-center num" data-testid="nav-chat-unread">{unread}</span>}
        </NavLink>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen flex print-area-only">
      <aside className="hidden lg:flex w-72 shrink-0 flex-col bg-[#1A4D2E] sticky top-0 h-screen overflow-y-auto">
        <div className="p-5 border-b border-white/10"><Brand logo={settings?.logo} name={settings?.org_name} /></div>
        {Side}
      </aside>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden no-print">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute right-0 top-0 h-full w-72 bg-[#1A4D2E] overflow-y-auto">
            <div className="p-5 flex items-center justify-between border-b border-white/10">
              <Brand logo={settings?.logo} name={settings?.org_name} />
              <button className="text-white" onClick={() => setOpen(false)} data-testid="mobile-nav-close"><X /></button>
            </div>
            {Side}
          </aside>
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-30 bg-[#FDFBF7]/90 backdrop-blur-md border-b border-border no-print">
          {!online && (
            <div className="bg-rose-700 text-white text-sm font-bold px-4 py-2 flex items-center gap-2" data-testid="offline-banner">
              <WifiOff className="w-4 h-4" /> لا يوجد اتصال بالإنترنت – لن تُحفظ أي عملية حتى عودة الاتصال
            </div>
          )}
          <div className="flex items-center gap-3 px-4 sm:px-6 h-16">
            <button className="lg:hidden k-btn-outline k-btn-sm" onClick={() => setOpen(true)} data-testid="mobile-nav-open"><Menu className="w-5 h-5" /></button>
            <div className="flex-1" />
            <div className="text-left leading-tight">
              <div className="font-bold text-sm" data-testid="header-user-name">{user?.full_name}</div>
              <div className="text-xs text-slate-500">{ROLE_LABEL[user?.role]}</div>
            </div>
            <button className="k-btn-outline k-btn-sm" title="تغيير كلمة المرور" onClick={() => nav("/change-password")} data-testid="change-password-link"><KeyRound className="w-4 h-4" /></button>
            <button className="k-btn-outline k-btn-sm" onClick={logout} data-testid="logout-btn"><LogOut className="w-4 h-4" /> خروج</button>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1500px] w-full mx-auto"><Outlet /></main>
      </div>
    </div>
  );
}
